import { useSeoMeta } from '@unhead/react';
import { useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  Menu,
  X,
  BookOpen,
  Cpu,
  Lock,
  MessageSquare,
  PenTool,
  ShieldCheck,
  Key,
  CheckCircle2,
  XCircle,
  Eye,
  Zap,
  Rocket,
  Server,
  Wifi,
  AlertCircle,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SiteFooter } from '@/components/SiteFooter';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */
const GithubIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Nori SVG Mascot                                                    */
/* ------------------------------------------------------------------ */
const NoriLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg
    viewBox="0 0 20 16"
    shapeRendering="crispEdges"
    aria-label="Nori the ostrich"
    className={className}
  >
    <g fill="#7B68EE">
      <rect x="8" y="0" width="2" height="1"/>
      <rect x="7" y="1" width="1" height="2"/>
      <rect x="10" y="1" width="1" height="2"/>
      <rect x="6" y="3" width="2" height="1"/>
      <rect x="10" y="3" width="2" height="1"/>
      <rect x="4" y="4" width="10" height="1"/>
      <rect x="3" y="5" width="1" height="3"/>
      <rect x="14" y="5" width="1" height="3"/>
      <rect x="7" y="6" width="2" height="1"/>
      <rect x="4" y="8" width="2" height="1"/>
      <rect x="12" y="8" width="2" height="1"/>
      <rect x="6" y="9" width="6" height="1"/>
      <rect x="6" y="10" width="1" height="1"/>
      <rect x="11" y="10" width="1" height="1"/>
      <rect x="5" y="11" width="2" height="1"/>
      <rect x="11" y="11" width="2" height="1"/>
      <rect x="5" y="12" width="1" height="2"/>
      <rect x="12" y="12" width="1" height="2"/>
      <rect x="4" y="14" width="3" height="1"/>
      <rect x="9" y="14" width="4" height="1"/>
      <rect x="3" y="15" width="1" height="1"/>
      <rect x="6" y="15" width="1" height="1"/>
      <rect x="9" y="15" width="1" height="1"/>
      <rect x="12" y="15" width="1" height="1"/>
    </g>
    <g fill="#C4BBFF">
      <rect x="8" y="1" width="2" height="2"/>
      <rect x="8" y="3" width="2" height="1"/>
      <rect x="4" y="5" width="10" height="1"/>
      <rect x="4" y="6" width="3" height="1"/>
      <rect x="9" y="6" width="5" height="1"/>
      <rect x="4" y="7" width="10" height="1"/>
      <rect x="6" y="8" width="6" height="1"/>
      <rect x="7" y="10" width="4" height="1"/>
      <rect x="7" y="11" width="4" height="1"/>
      <rect x="6" y="12" width="6" height="1"/>
      <rect x="6" y="13" width="1" height="1"/>
      <rect x="9" y="13" width="3" height="1"/>
    </g>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Navigation                                                         */
/* ------------------------------------------------------------------ */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', href: '/' },
    { label: 'Stack', href: '/stack' },
    { label: 'Get Started', href: '/get-started' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background/80 backdrop-blur-xl border-b border-border/50'
          : 'bg-transparent'
      }`}
    >
      <div className="container grid grid-cols-[1fr_1fr] lg:grid-cols-[1fr_auto_1fr] items-center h-16">
        <div className="flex items-center">
          <a href="/" className="flex items-center gap-2.5 group shrink-0">
            <NoriLogo className="w-7 h-7 transition-transform group-hover:scale-110 shrink-0" />
            <span className="font-semibold text-lg tracking-tight whitespace-nowrap">
              nostr-station
            </span>
          </a>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-3">
          <a
            href="https://github.com/jared-logan/nostr-station"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex"
          >
            <button className="inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors">
              <GithubIcon className="w-5 h-5" />
            </button>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-border/50">
          <nav className="container py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50"
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://github.com/jared-logan/nostr-station"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50 flex items-center gap-2"
            >
              <GithubIcon className="w-4 h-4" />
              GitHub
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

/* ------------------------------------------------------------------ */
/*  Hero Section                                                       */
/* ------------------------------------------------------------------ */
const HeroSection = () => {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-16">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,hsl(265_85%_60%/0.15),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_600px_at_50%_50%,hsl(265_70%_55%/0.08),transparent)]" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(hsl(265 85% 60%) 1px, transparent 1px), linear-gradient(90deg, hsl(265 85% 60%) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Nori
          </span>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
            Your AI dev partner, <span className="text-gradient">on your terms</span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Project-aware chat-driven coding built into the dashboard. Multi-provider, keys in your OS keychain, signing via Amber.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  01 — The Pitch                                                     */
/* ------------------------------------------------------------------ */
const pitchCards = [
  {
    icon: BookOpen,
    title: 'Project-aware',
    description: 'Nori reads your README, custom context overlays, and project capability badges automatically. No copy-pasting code in for context.',
    color: 'hsl(265 85% 60%)',
  },
  {
    icon: Cpu,
    title: 'Multi-provider, your choice',
    description: 'Anthropic, OpenAI, OpenRouter, Ollama, LM Studio, Groq, OpenCode Zen, PayPerQ, Routstr. Run local models when code should never leave your machine.',
    color: 'hsl(280 85% 60%)',
  },
  {
    icon: Lock,
    title: 'Keys never on disk',
    description: 'AI keys live in your OS keychain (macOS Keychain, GNOME Keyring, or AES-256-GCM fallback). No .env files, no plaintext.',
    color: 'hsl(295 85% 60%)',
  },
];

const PitchSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pitch" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.06),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            01 — The Pitch
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Why Nori, <span className="text-gradient">not just another AI chat</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {pitchCards.map((card, index) => (
            <div
              key={card.title}
              className={`group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${card.color}15` }}
              >
                <card.icon className="w-6 h-6" style={{ color: card.color }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  02 — How It Works                                                  */
/* ------------------------------------------------------------------ */
const howItWorksSteps = [
  {
    icon: MessageSquare,
    title: 'Open Chat',
    description: 'Click the Chat panel in the dashboard for any project. Nori loads project context automatically.',
  },
  {
    icon: PenTool,
    title: 'Describe what you want',
    description: 'Plain language. Nori plans the edit, shows you the diff before applying.',
  },
  {
    icon: ShieldCheck,
    title: 'Review and approve',
    description: 'Per-project permission mode controls how much Nori does on its own.',
  },
  {
    icon: Key,
    title: 'Sign and commit',
    description: "Amber prompts on your phone. The commit message includes Nori's reasoning so future-you knows why.",
  },
];

const HowItWorksSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            02 — How it works
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Chat to commit, <span className="text-gradient">in four steps</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {howItWorksSteps.map((step, index) => (
            <div
              key={step.title}
              className={`group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                <step.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  03 — Providers                                                     */
/* ------------------------------------------------------------------ */
const providers = [
  { name: 'Anthropic (Claude)', keysLocal: true, runsLocal: false },
  { name: 'OpenAI', keysLocal: true, runsLocal: false },
  { name: 'OpenRouter', keysLocal: true, runsLocal: false },
  { name: 'Groq', keysLocal: true, runsLocal: false },
  { name: 'OpenCode Zen', keysLocal: true, runsLocal: false },
  { name: 'PayPerQ', keysLocal: true, runsLocal: false },
  { name: 'Routstr', keysLocal: true, runsLocal: false },
  { name: 'Ollama', keysLocal: true, runsLocal: true },
  { name: 'LM Studio', keysLocal: true, runsLocal: true },
];

const ProvidersSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="providers" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.06),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            03 — Providers
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Bring your own <span className="text-gradient">model</span>
          </h2>
        </div>

        <div className={`max-w-2xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      Provider
                    </th>
                    <th className="text-center px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      Keys stay local
                    </th>
                    <th className="text-center px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      Runs local
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map((provider, index) => (
                    <tr
                      key={provider.name}
                      className={`border-b border-border/30 hover:bg-primary/5 transition-colors ${index === providers.length - 1 ? 'border-b-0' : ''}`}
                    >
                      <td className="px-6 py-4 text-foreground font-medium">
                        {provider.name}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {provider.keysLocal ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {provider.runsLocal ? (
                          <span className="text-sm text-emerald-500">Your machine</span>
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4 text-center">
            Keys for API providers are stored in your OS keychain. Local-model providers don't need keys.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  04 — Permission Modes                                              */
/* ------------------------------------------------------------------ */
const permissionModes = [
  {
    icon: Eye,
    title: 'Read-only',
    description: 'Analyze, suggest, explain. Cannot modify files.',
    color: 'hsl(265 85% 60%)',
  },
  {
    icon: Zap,
    title: 'Auto-edit',
    description: 'Apply diffs, but every edit shows in the dashboard activity log. Good for everyday development.',
    color: 'hsl(280 85% 60%)',
  },
  {
    icon: Rocket,
    title: 'YOLO',
    description: 'Apply edits and run commands without prompting. Per-project setting — turn it on only where it makes sense.',
    color: 'hsl(295 85% 60%)',
  },
];

const PermissionModesSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="permissions" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            04 — Permission Modes
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            How much autonomy <span className="text-gradient">do you want?</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {permissionModes.map((mode, index) => (
            <div
              key={mode.title}
              className={`group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${mode.color}15` }}
              >
                <mode.icon className="w-6 h-6" style={{ color: mode.color }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">{mode.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {mode.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  05 — Privacy Posture                                               */
/* ------------------------------------------------------------------ */
const PrivacySection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="privacy" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.06),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            05 — Privacy Posture
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            What leaves <span className="text-gradient">your machine</span>
          </h2>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          <div className={`relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '100ms' }}>
            <div className="flex items-start gap-4">
              <Server className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold mb-1">API providers</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your prompts and code diffs go to the provider you chose. No nostr-station server in between.
                </p>
              </div>
            </div>
          </div>

          <div className={`relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '200ms' }}>
            <div className="flex items-start gap-4">
              <Wifi className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold mb-1">Local providers (Ollama, LM Studio)</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Nothing leaves your machine.
                </p>
              </div>
            </div>
          </div>

          <div className={`relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '300ms' }}>
            <div className="flex items-start gap-4">
              <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold mb-1">Either way</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  API keys stay in your OS keychain, Amber signs any Nostr events Nori generates, no telemetry from nostr-station.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  06 — What Nori Isn't                                               */
/* ------------------------------------------------------------------ */
const WhatNoriIsntSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="what-nori-isnt" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            06 — Honest about scope
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            What Nori <span className="text-gradient">isn't</span>
          </h2>
        </div>

        <div className={`max-w-3xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-6 sm:p-8">
            <div className="absolute -top-3 left-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                <AlertCircle className="w-3.5 h-3.5" />
                Clarification
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed pt-2">
              Nori is the chat-driven dev surface in the dashboard. It's not a replacement for full terminal agents like Claude Code or OpenCode — those are wired into the embedded terminal when you need shell access or heavier autonomy. Nori is not autonomous either; every commit requires your phone-approved Amber signature. And it depends on whichever provider you've configured — Nori is the surface, not the model. For social Nostr — reading your feed, posting notes, replying to people — head to the Client panel (embedded Ditto). Nori is the dev chat, the Client is the social surface.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  CTA Section                                                        */
/* ------------------------------------------------------------------ */
const CtaSection = () => {
  return (
    <section className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.08),transparent)]" />

      <div className="container relative text-center">
        <Sparkles className="w-12 h-12 text-primary mx-auto mb-6" />
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
          Install nostr-station to try <span className="text-gradient">Nori</span>
        </h2>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
          One command gets you a complete local Nostr stack with Nori ready to pair with your favorite AI provider.
        </p>
        <a href="/get-started">
          <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 text-base">
            Get Started
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </a>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */
const Nori = () => {
  useSeoMeta({
    title: 'Nori — Your AI dev partner, on your terms — nostr-station',
    description: 'Project-aware chat-driven coding built into the dashboard. Multi-provider, keys in your OS keychain, signing via Amber.',
    ogTitle: 'Nori — Your AI dev partner, on your terms — nostr-station',
    ogDescription: 'Project-aware chat-driven coding built into the dashboard. Multi-provider, keys in your OS keychain, signing via Amber.',
    twitterCard: 'summary_large_image',
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      <HeroSection />
      <PitchSection />
      <HowItWorksSection />
      <ProvidersSection />
      <PermissionModesSection />
      <PrivacySection />
      <WhatNoriIsntSection />
      <CtaSection />
      <SiteFooter />
    </div>
  );
};

export default Nori;
